import { useParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BusyButton } from "@/components/acquisitions/BusyButton";
import { AlertTriangle, Save, Send, Trash2 } from "lucide-react";
import { Loader2 } from "lucide-react";
import { RequestTitleFields } from "./request-form/RequestTitleFields";
import { RequestAcquisitionFields } from "./request-form/RequestAcquisitionFields";
import { useAcquisitionRequestForm } from "./request-form/useAcquisitionRequestForm";

export default function NewAcquisitionRequestPage() {
  const { id: editId } = useParams<{ id: string }>();
  const formState = useAcquisitionRequestForm(editId);
  const busy = formState.pending !== null;

  if (formState.loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title={formState.isEdit ? "Edit request" : "New Purchase Request"}
        description="Request an acquisition for the library"
        breadcrumbItems={[
          { label: "Requests", href: "/acquisitions/requests" },
          { label: formState.isEdit ? "Edit request" : "New Purchase Request" },
        ]}
        showSearch={false}
        titleSize="md"
      />

      {formState.duplicates && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Potential duplicates found:</strong>
            {formState.duplicates.existingBiblios.length > 0 && (
              <span className="mt-1 block">
                Already in catalog:{" "}
                {formState.duplicates.existingBiblios.map((biblio) => biblio.title).join(", ")}
              </span>
            )}
            {formState.duplicates.openRequests.length > 0 && (
              <span className="mt-1 block">
                Open requests:{" "}
                {formState.duplicates.openRequests
                  .map((request) => `${request.title} (${request.status})`)
                  .join(", ")}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      <RequestTitleFields
        form={formState.form}
        onChange={formState.patch}
        onIsbnBlur={formState.checkDuplicates}
      />
      <RequestAcquisitionFields
        form={formState.form}
        funds={formState.funds}
        onChange={formState.patch}
      />

      <div className="flex flex-wrap justify-end gap-3">
        {formState.isEdit && (
          <BusyButton
            type="button"
            variant="destructive"
            className="mr-auto"
            busy={formState.pending === "delete"}
            disabled={busy}
            icon={<Trash2 className="mr-2 h-4 w-4" />}
            onClick={formState.handleDeleteDraft}
          >
            Delete draft
          </BusyButton>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => formState.navigate("/acquisitions/requests")}
          disabled={busy}
        >
          Cancel
        </Button>
        <BusyButton
          variant="outline"
          busy={formState.pending === "draft"}
          disabled={busy}
          icon={<Save className="mr-2 h-4 w-4" />}
          onClick={() => formState.handleSubmit(false)}
        >
          Save Draft
        </BusyButton>
        <BusyButton
          busy={formState.pending === "submit"}
          disabled={busy}
          icon={<Send className="mr-2 h-4 w-4" />}
          onClick={() => formState.handleSubmit(true)}
        >
          Submit for Review
        </BusyButton>
      </div>
    </PageContainer>
  );
}
